/**
 * Agregação de atendimentos para a Análise de Vendas IA.
 * Foco em métricas de decisão (origem, SLA), não em jargão técnico.
 */

import type { ChatStats } from '../../../services/modules/gemini/salesAnalysis';
import type { DateRange } from '../../../components/shared/filters/DateRangePicker';

export interface AnalysisConversationSummary {
    id: string;
    assignedTo?: string;
    /** DB pode usar "open"; o UI trata "closed" como fechado. */
    status?: 'active' | 'closed' | 'open' | string;
    lastMessageTime?: Date | string | null;
    /** Origem de negócio já resolvida (CRM / Meta Ads / Sem origem). */
    originLabel?: string;
    /**
     * Minutos até a 1ª resposta da equipe.
     * `null` = cliente falou e equipe ainda não respondeu.
     * `undefined` = sem mensagem do cliente mensurável.
     */
    firstResponseMinutes?: number | null;
}

function median(values: number[]): number | null {
    if (!values.length) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) {
        return Math.round((sorted[mid - 1] + sorted[mid]) / 2);
    }
    return Math.round(sorted[mid]);
}

export function buildChatStats(
    conversations: AnalysisConversationSummary[],
    userNameMap: Map<string, string>
): ChatStats | undefined {
    if (!conversations.length) return undefined;

    const totalConversations = conversations.length;
    const closedConversations = conversations.filter((c) => c.status === 'closed').length;
    const openConversations = totalConversations - closedConversations;
    const unassignedConversations = conversations.filter((c) => !c.assignedTo).length;

    const agentCountMap = new Map<string, number>();
    conversations.forEach((c) => {
        if (!c.assignedTo) return;
        const agentName = userNameMap.get(c.assignedTo) || 'Desconhecido';
        agentCountMap.set(agentName, (agentCountMap.get(agentName) || 0) + 1);
    });

    const conversationsByAgent = Array.from(agentCountMap.entries())
        .map(([agentName, count]) => ({ agentName, count }))
        .sort((a, b) => b.count - a.count);

    const porOrigem: Record<string, number> = {};
    conversations.forEach((c) => {
        const label = c.originLabel || 'Sem origem';
        porOrigem[label] = (porOrigem[label] || 0) + 1;
    });

    const answeredMinutes: number[] = [];
    let conversasSemRespostaDaEquipe = 0;
    let slaEligible = 0;
    let within15 = 0;

    conversations.forEach((c) => {
        if (c.firstResponseMinutes === undefined) return;
        slaEligible += 1;
        if (c.firstResponseMinutes === null) {
            conversasSemRespostaDaEquipe += 1;
            return;
        }
        answeredMinutes.push(c.firstResponseMinutes);
        if (c.firstResponseMinutes <= 15) within15 += 1;
    });

    const pctRespondidasEmAte15Min =
        slaEligible > 0 ? Math.round((within15 / slaEligible) * 100) : null;

    return {
        totalConversations,
        openConversations,
        closedConversations,
        unassignedConversations,
        conversationsByAgent,
        origemDasConversas: porOrigem,
        tempoMedianoPrimeiraRespostaMinutos: median(answeredMinutes),
        pctRespondidasEmAte15Min,
        conversasSemRespostaDaEquipe,
    };
}

function inDateRange(
    lastMessageTime: Date | string | null | undefined,
    dateRange?: DateRange
): boolean {
    if (!dateRange?.start || !dateRange?.end) return true;
    if (!lastMessageTime) return false;
    const ts = lastMessageTime instanceof Date ? lastMessageTime : new Date(lastMessageTime);
    if (Number.isNaN(ts.getTime())) return false;
    return ts >= dateRange.start && ts <= dateRange.end;
}

export function filterConversationsForAnalysis(
    conversations: AnalysisConversationSummary[],
    options: {
        dateRange?: DateRange;
        currentUserId?: string;
        canViewAllConversations: boolean;
    }
): AnalysisConversationSummary[] {
    const { dateRange, currentUserId, canViewAllConversations } = options;

    return conversations.filter((c) => {
        if (!inDateRange(c.lastMessageTime, dateRange)) return false;
        if (canViewAllConversations) return true;
        if (!currentUserId) return false;
        return !c.assignedTo || c.assignedTo === currentUserId;
    });
}
